import { mutationOptions, useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { closeModal } from 'src/navigation/Lnk'
import { QueryError } from 'src/queries/network.utils'
import {
  postCreateOrganization,
  PostOrganizationSuccessPayload,
  recoverBackendErrorPayload,
} from 'src/queries/organizations/organizationCreate.network'
import { useInviteToOrganization } from 'src/queries/organizations/organizationInvites.queries'
import { getMe } from 'src/redux/actions/auth.actions'
import {
  POST_ORGANIZATION_FAILURE,
  POST_ORGANIZATION_INIT,
  POST_ORGANIZATION_SUCCESS,
} from 'src/redux/actions/data/organizations.actions'
import { PostOrganizationRequest } from 'src/requests/postOrganization.request'

export type CreateOrganizationParams = {
  body: Partial<PostOrganizationRequest>
  invitedMembers?: string[]
  // Runs instead of the default closeModal() on success — e.g. RegisterOrganization/SetupOrganization close their own popup and show a confirmation toast instead.
  customCloseModalOnSuccess?: () => void
}

export const organizationCreateMutations = {
  create: () =>
    mutationOptions<
      PostOrganizationSuccessPayload,
      QueryError,
      CreateOrganizationParams
    >({
      mutationFn: (params) => postCreateOrganization(params.body),
    }),
}

// No RQ-backed read for this domain — refresh flows through dispatch(getMe()) (src/queries/AGENTS.md).
export const useCreateOrganization = () => {
  const dispatch = useDispatch()
  const inviteToOrganization = useInviteToOrganization()

  return useMutation<
    PostOrganizationSuccessPayload,
    QueryError,
    CreateOrganizationParams
  >({
    ...organizationCreateMutations.create(),
    onMutate: () => {
      dispatch({ type: POST_ORGANIZATION_INIT })
    },
    onSuccess: (data, variables) => {
      dispatch({ type: POST_ORGANIZATION_SUCCESS, payload: data })
      dispatch(getMe())
      if (
        variables.invitedMembers !== undefined &&
        variables.invitedMembers.length > 0
      ) {
        // Silent invite: redux-syncs INIT/SUCCESS/FAILURE only — the surrounding flow owns toast/closeModal for org creation itself.
        inviteToOrganization.mutate({
          organizationId: data.organization.id,
          body: { emails: variables.invitedMembers },
          silent: true,
        })
      }
      // Intentional AGENTS.md deviation (close/toast belong on `mutate`): these are
      // global actions the thunk fired regardless of unmount; `mutate` would drop them.
      if (variables.customCloseModalOnSuccess !== undefined) {
        variables.customCloseModalOnSuccess()
      } else {
        dispatch(closeModal())
      }
    },
    onError: (error) => {
      dispatch({
        type: POST_ORGANIZATION_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}
